import { useCallback, useEffect, useRef, useState } from 'react';

export default function useDraggable({ draggableRef, initialPosition }) {
  const [position, setPosition] = useState(initialPosition);
  const dragStateRef = useRef({ dragging: false, lastX: 0, lastY: 0 });

  const getViewportSize = useCallback(() => {
    if (window.visualViewport) {
      return {
        width: window.visualViewport.width,
        height: window.visualViewport.height,
      };
    }

    return {
      width: document.documentElement.clientWidth,
      height: document.documentElement.clientHeight,
    };
  }, []);

  const getBoundedPosition = useCallback(
    nextPosition => {
      const element = draggableRef.current;
      const width = element ? element.offsetWidth : 0;
      const height = element ? element.offsetHeight : 0;
      const viewportSize = getViewportSize();
      const maxX = Math.max(viewportSize.width - width, 0);
      const maxY = Math.max(viewportSize.height - height, 0);

      return {
        x: Math.min(Math.max(nextPosition.x, 0), maxX),
        y: Math.min(Math.max(nextPosition.y, 0), maxY),
      };
    },
    [draggableRef, getViewportSize]
  );

  const updateElementPosition = useCallback(
    nextPosition => {
      const element = draggableRef.current;
      if (element) {
        element.style.position = 'fixed';
        element.style.transform = `translate(${nextPosition.x}px, ${nextPosition.y}px)`;
      }
    },
    [draggableRef]
  );

  const moveBy = useCallback(
    (dx, dy) => {
      setPosition(prevPosition => {
        const nextPosition = getBoundedPosition({
          x: prevPosition.x + dx,
          y: prevPosition.y + dy,
        });

        updateElementPosition(nextPosition);
        return nextPosition;
      });
    },
    [getBoundedPosition, updateElementPosition]
  );

  const onPointerMove = useCallback(
    event => {
      if (!dragStateRef.current.dragging) return;

      event.preventDefault();
      const dx = event.clientX - dragStateRef.current.lastX;
      const dy = event.clientY - dragStateRef.current.lastY;
      dragStateRef.current.lastX = event.clientX;
      dragStateRef.current.lastY = event.clientY;
      moveBy(dx, dy);
    },
    [moveBy]
  );

  const onPointerUp = useCallback(() => {
    dragStateRef.current.dragging = false;
    window.removeEventListener('pointermove', onPointerMove);
    window.removeEventListener('pointerup', onPointerUp);
  }, [onPointerMove]);

  const onPointerDown = useCallback(
    event => {
      if (event.button !== undefined && event.button !== 0) return;

      event.preventDefault();
      dragStateRef.current = {
        dragging: true,
        lastX: event.clientX,
        lastY: event.clientY,
      };
      window.addEventListener('pointermove', onPointerMove);
      window.addEventListener('pointerup', onPointerUp);
    },
    [onPointerMove, onPointerUp]
  );

  const onTouchMove = useCallback(
    event => {
      if (!dragStateRef.current.dragging) return;

      const touch = event.touches[0];
      if (!touch) return;

      const dx = touch.clientX - dragStateRef.current.lastX;
      const dy = touch.clientY - dragStateRef.current.lastY;
      dragStateRef.current.lastX = touch.clientX;
      dragStateRef.current.lastY = touch.clientY;
      moveBy(dx, dy);
      event.preventDefault();
    },
    [moveBy]
  );

  const onTouchEnd = useCallback(() => {
    dragStateRef.current.dragging = false;
    window.removeEventListener('touchmove', onTouchMove);
    window.removeEventListener('touchend', onTouchEnd);
  }, [onTouchMove]);

  const onTouchStart = useCallback(
    event => {
      if (window.PointerEvent) return;

      const touch = event.touches[0];
      if (!touch) return;

      event.preventDefault();
      dragStateRef.current = {
        dragging: true,
        lastX: touch.clientX,
        lastY: touch.clientY,
      };
      window.addEventListener('touchmove', onTouchMove, { passive: false });
      window.addEventListener('touchend', onTouchEnd);
    },
    [onTouchMove, onTouchEnd]
  );

  const onMouseMove = useCallback(
    event => {
      if (!dragStateRef.current.dragging) return;

      event.preventDefault();
      const dx = event.clientX - dragStateRef.current.lastX;
      const dy = event.clientY - dragStateRef.current.lastY;
      dragStateRef.current.lastX = event.clientX;
      dragStateRef.current.lastY = event.clientY;
      moveBy(dx, dy);
    },
    [moveBy]
  );

  const onMouseUp = useCallback(() => {
    dragStateRef.current.dragging = false;
    document.removeEventListener('mousemove', onMouseMove);
    document.removeEventListener('mouseup', onMouseUp);
  }, [onMouseMove]);

  const onMouseDown = useCallback(
    event => {
      if (window.PointerEvent) return;
      if (event.button !== undefined && event.button !== 0) return;

      event.preventDefault();
      dragStateRef.current = {
        dragging: true,
        lastX: event.clientX,
        lastY: event.clientY,
      };
      document.addEventListener('mousemove', onMouseMove);
      document.addEventListener('mouseup', onMouseUp);
    },
    [onMouseMove, onMouseUp]
  );

  useEffect(
    () => () => {
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
      window.removeEventListener('touchmove', onTouchMove);
      window.removeEventListener('touchend', onTouchEnd);
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    },
    [
      onPointerMove,
      onPointerUp,
      onTouchMove,
      onTouchEnd,
      onMouseMove,
      onMouseUp,
    ]
  );

  useEffect(() => {
    const keepWithinWindow = () => {
      setPosition(prevPosition => {
        const nextPosition = getBoundedPosition(prevPosition);
        updateElementPosition(nextPosition);
        return nextPosition;
      });
    };

    keepWithinWindow();
    window.addEventListener('resize', keepWithinWindow);
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', keepWithinWindow);
      window.visualViewport.addEventListener('scroll', keepWithinWindow);
    }

    return () => {
      window.removeEventListener('resize', keepWithinWindow);
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', keepWithinWindow);
        window.visualViewport.removeEventListener('scroll', keepWithinWindow);
      }
    };
  }, [getBoundedPosition, updateElementPosition]);

  return {
    position,
    dragHandlers: {
      onMouseDown,
      onPointerDown,
      onTouchStart,
    },
  };
}
